import { useState, useRef, useContext } from 'react';
import { Box, Button, FormControl, FormLabel, Input, Stack, Typography, Alert } from '@mui/joy';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import EmailRoundedIcon         from '@mui/icons-material/EmailRounded';
import LockRoundedIcon          from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon    from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { AppContext } from '../context';


// ─── Сетка ────────────────────────────────────────────────────────────────────
function GridBg() {
    return (
        <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: `
                linear-gradient(var(--joy-palette-divider) 1px, transparent 1px),
                linear-gradient(90deg, var(--joy-palette-divider) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
        }} />
    );
}

// ─── Глоу-орб ─────────────────────────────────────────────────────────────────
function GlowOrb({ x, y, size, color, blur }) {
    return (
        <Box sx={{
            position: 'absolute', left: x, top: y,
            width: size, height: size, borderRadius: '50%',
            background: color, filter: `blur(${blur})`,
            pointerEvents: 'none', opacity: 0.55,
        }} />
    );
}

// ─── 3D-карточка ─────────────────────────────────────────────────────────────
function TiltCard({ children }) {
    const ref = useRef(null);
    const mx  = useMotionValue(0);
    const my  = useMotionValue(0);
    const rx  = useTransform(my, [-0.5, 0.5], [4, -4]);
    const ry  = useTransform(mx, [-0.5, 0.5], [-4, 4]);

    return (
        <motion.div
            ref={ref}
            onMouseMove={(e) => {
                const r = ref.current?.getBoundingClientRect();
                if (!r) return;
                mx.set((e.clientX - r.left) / r.width - 0.5);
                my.set((e.clientY - r.top)  / r.height - 0.5);
            }}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d', perspective: 900 }}
            transition={{ type: 'spring', stiffness: 280, damping: 28 }}
        >
            {children}
        </motion.div>
    );
}

// ─── Анимация ошибки поля ─────────────────────────────────────────────────────
function FieldError({ msg }) {
    return (
        <AnimatePresence>
            {msg && (
                <motion.p
                    initial={{ opacity: 0, y: -4, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -4, height: 0 }}
                    transition={{ duration: 0.17 }}
                    style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--joy-palette-danger-500)' }}
                >
                    {msg}
                </motion.p>
            )}
        </AnimatePresence>
    );
}

// ─── Стиль инпута ─────────────────────────────────────────────────────────────
const inputSx = (err) => ({
    bgcolor: 'rgba(255,255,255,0.03)',
    border: `1px solid ${err
        ? 'var(--joy-palette-danger-400)'
        : 'rgba(162,38,226,0.22)'}`,
    borderRadius: 'var(--joy-radius-md)',
    fontSize: 15,
    py: 1.1,
    transition: 'border .2s, box-shadow .2s',
    '&:hover': {
        borderColor: err
            ? 'var(--joy-palette-danger-400)'
            : 'rgba(162,38,226,0.5)',
    },
    '&:focus-within': {
        borderColor: err
            ? 'var(--joy-palette-danger-400)'
            : 'var(--joy-palette-primary-500)',
        boxShadow: err
            ? '0 0 0 3px rgba(248,113,113,0.15)'
            : '0 0 0 3px rgba(162,38,226,0.18)',
    },
    '--Input-placeholderColor': 'var(--joy-palette-text-tertiary)',
    '& input': { color: 'var(--joy-palette-text-primary)' },
});

const labelSx = {
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--joy-palette-text-secondary)', mb: 0.5,
};

// ════════════════════════════════════════════════════════════════════
//  Компонент LoginPage
// ════════════════════════════════════════════════════════════════════

export default function LoginPage() {
    const { login } = useContext(AppContext);

    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [showPwd,  setShowPwd]  = useState(false);
    const [loading,  setLoading]  = useState(false);
    const [apiError, setApiError] = useState('');
    const [errors,   setErrors]   = useState({});

    // ── Валидация ──────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!email.trim())                      e.email    = 'Введи email';
        else if (!/\S+@\S+\.\S+/.test(email))   e.email    = 'Некорректный email';
        if (!password)                           e.password = 'Введи пароль';
        else if (password.length < 6)            e.password = 'Минимум 6 символов';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        setApiError('');
        if (!validate()) return;
        setLoading(true);
        const res = await login(email.trim().toLowerCase(), password);
        if (!res.ok) setApiError(res.error || 'Ошибка');
        setLoading(false);
    };

    // ── Варианты анимации ──────────────────────────────────────────
    const containerVariants = {
        hidden:  { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.09 } },
    };
    const itemVariants = {
        hidden:  { opacity: 0, y: 18 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: 'var(--joy-palette-background-body)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', overflow: 'hidden', p: 2,
        }}>
            {/* Фон */}
            <GridBg />
            <GlowOrb
                x="-10%" y="-10%" size="500px"
                color="radial-gradient(circle, rgba(162,38,226,0.35) 0%, transparent 70%)"
                blur="60px"
            />
            <GlowOrb
                x="60%" y="60%" size="400px"
                color="radial-gradient(circle, rgba(178,74,239,0.2) 0%, transparent 70%)"
                blur="80px"
            />

            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}
            >
                {/* Логотип */}
                <motion.div variants={itemVariants} style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 1.5,
                    }}>
                        {/* Лого из файла */}
                        <Box
                            component="img"
                            src="/logo.png"
                            alt="Фото G"
                            sx={{
                                width: 55,
                                // borderRadius: 'var(--joy-radius-lg)',
                                objectFit: 'cover',
                                // boxShadow: '0 8px 24px rgba(162,38,226,0.45)',
                            }}
                        />
                        <Typography sx={{
                            fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em',
                            background: 'linear-gradient(135deg, var(--joy-palette-text-primary) 0%, var(--joy-palette-primary-300) 100%)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>
                            Фото G
                        </Typography>
                    </Box>
                    <Typography sx={{
                        fontSize: 13,
                        color: 'var(--joy-palette-text-secondary)',
                        letterSpacing: '0.03em',
                    }}>
                        Система для продвинутых фотографов
                    </Typography>
                </motion.div>

                {/* Карточка */}
                <motion.div variants={itemVariants}>
                    <TiltCard>
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            p: { xs: 3, sm: 4 },
                            backdropFilter: 'blur(20px)',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px var(--joy-palette-divider)',
                        }}>
                            <Stack spacing={2.5}>
                                {/* Email */}
                                <FormControl>
                                    <FormLabel sx={labelSx}>Email</FormLabel>
                                    <Input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }}
                                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                        startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-secondary)' }} />}
                                        sx={inputSx(errors.email)}
                                    />
                                    <FieldError msg={errors.email} />
                                </FormControl>

                                {/* Пароль */}
                                <FormControl>
                                    <FormLabel sx={labelSx}>Пароль</FormLabel>
                                    <Input
                                        type={showPwd ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={e => { setPassword(e.target.value); setErrors(p => ({ ...p, password: '' })); }}
                                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                        startDecorator={<LockRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-secondary)' }} />}
                                        endDecorator={
                                            <Box
                                                onClick={() => setShowPwd(v => !v)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'var(--joy-palette-text-secondary)',
                                                    '&:hover': { color: 'var(--joy-palette-primary-400)' },
                                                    display: 'flex',
                                                    transition: 'color .15s',
                                                }}
                                            >
                                                {showPwd
                                                    ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} />
                                                    : <VisibilityRoundedIcon    sx={{ fontSize: 18 }} />}
                                            </Box>
                                        }
                                        sx={inputSx(errors.password)}
                                    />
                                    <FieldError msg={errors.password} />
                                </FormControl>

                                {/* API ошибка */}
                                <AnimatePresence>
                                    {apiError && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                        >
                                            <Alert
                                                color="danger" variant="soft" size="sm"
                                                sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 13 }}
                                            >
                                                {typeof apiError === 'string'
                                                    ? apiError
                                                    : JSON.stringify(apiError)}
                                            </Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Кнопка */}
                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Button
                                        fullWidth
                                        loading={loading}
                                        onClick={handleSubmit}
                                        sx={{
                                            py: 1.4,
                                            borderRadius: 'var(--joy-radius-md)',
                                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                            color: '#fff', fontWeight: 700, fontSize: 14,
                                            letterSpacing: '0.03em',
                                            border: 'none',
                                            boxShadow: '0 8px 24px rgba(162,38,226,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, var(--joy-palette-primary-600) 0%, var(--joy-palette-primary-400) 100%)',
                                                boxShadow: '0 12px 32px rgba(162,38,226,0.55)',
                                            },
                                            transition: 'all .2s',
                                        }}
                                    >
                                        Войти
                                    </Button>
                                </motion.div>
                            </Stack>
                        </Box>
                    </TiltCard>
                </motion.div>

                {/* Подпись */}
                <motion.div variants={itemVariants} style={{ textAlign: 'center', marginTop: 20 }}>
                    <Typography sx={{
                        fontSize: 11,
                        color: 'var(--joy-palette-text-tertiary)',
                        letterSpacing: '0.04em',
                        opacity: 0.7,
                    }}>
                        🔒 Защищённое соединение · Фото G v1.0
                    </Typography>
                </motion.div>
            </motion.div>
        </Box>
    );
}